import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, ArrowRight, FileText, ShieldCheck, ImagePlus, FlaskConical } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";
import { siteConfig } from "@/config/siteConfig";
import { trackProductView, trackCtaClick } from "@/lib/analytics";
import {
  findProduct,
  relatedProducts,
  CHARACTERISTIC_FIELDS,
} from "@/data/productsData";

const IMG_BASE = "/images/home";

// Hidden inline HTML comment so a non-technical reviewer can grep the rendered
// page source for `placeholder: replace with actual` to find each hero image.
const PlaceholderMarker = ({ productName, variant = "photograph" }) => (
  <span
    aria-hidden="true"
    style={{ display: "none" }}
    dangerouslySetInnerHTML={{
      __html: `<!-- placeholder: replace with actual ${productName} ${variant} -->`,
    }}
  />
);

const ProductImage = ({ imageKey, alt, productName }) => (
  <>
    <PlaceholderMarker productName={productName} />
    <picture>
      <source
        type="image/webp"
        srcSet={`${IMG_BASE}/${imageKey}-800.webp 800w, ${IMG_BASE}/${imageKey}.webp 1264w`}
        sizes="(min-width: 1024px) 720px, 100vw"
      />
      <img
        src={`${IMG_BASE}/${imageKey}.jpg`}
        alt={alt}
        loading="eager"
        decoding="async"
        width="1264"
        height="848"
        data-placeholder-image="true"
        className="w-full h-auto"
      />
    </picture>
  </>
);

/**
 * Product gallery — main image + 3 thumbnails (§8, Blister Pack Violet Ink only).
 * Real per-slot images will arrive later. Until then each slot points to the
 * same placeholder but with a distinct `?v=<slot>` suffix so the rendered
 * `<img src>` attribute string differs per slot — makes it obvious in the DOM
 * (and to testers) that the swap fired. When real photography is supplied,
 * swap the imageKey values below and drop the version suffix.
 */
const ProductGallery = ({ product }) => {
  const slots = [
    { key: "main",      imageKey: product.heroImageKey, alt: product.heroAlt,                                          variant: "product photograph (main)" },
    { key: "label",     imageKey: product.heroImageKey, alt: `${product.name} — product label`,                        variant: "product photograph (label)" },
    { key: "print",     imageKey: product.heroImageKey, alt: `${product.name} — print result on substrate`,            variant: "product photograph (print result)" },
    { key: "substrate", imageKey: product.heroImageKey, alt: `${product.name} — substrate detail`,                     variant: "product photograph (substrate)" },
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const current = slots[activeIndex];
  // Per-slot version suffix so the `<img src>` attribute changes when a
  // thumbnail is clicked, even while all placeholders share one file.
  const srcSuffix = `?v=${current.key}`;
  return (
    <div data-testid="product-gallery">
      <span
        aria-hidden="true"
        style={{ display: "none" }}
        dangerouslySetInnerHTML={{
          __html: `<!-- placeholder: replace with actual product photograph (main / label / print result / substrate) -->`,
        }}
      />
      <div className="image-frame" data-testid="product-gallery-main">
        <PlaceholderMarker productName={product.name} variant={current.variant} />
        <picture>
          <source
            type="image/webp"
            srcSet={`${IMG_BASE}/${current.imageKey}-800.webp${srcSuffix} 800w, ${IMG_BASE}/${current.imageKey}.webp${srcSuffix} 1264w`}
            sizes="(min-width: 1024px) 720px, 100vw"
          />
          <img
            key={current.key}
            src={`${IMG_BASE}/${current.imageKey}.jpg${srcSuffix}`}
            alt={current.alt}
            loading="eager"
            decoding="async"
            width="1264"
            height="848"
            data-placeholder-image="true"
            data-active-slot={current.key}
            className="w-full h-auto"
          />
        </picture>
      </div>
      <div className="mt-4 grid grid-cols-4 gap-3" data-testid="product-gallery-thumbs">
        {slots.map((s, i) => {
          const isActive = i === activeIndex;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setActiveIndex(i)}
              aria-label={`Show ${s.key} view`}
              aria-pressed={isActive}
              className={`aspect-square border rounded-[4px] flex items-center justify-center bg-[var(--bg-alt)] text-[var(--slate)] transition-colors ${
                isActive
                  ? "border-[var(--navy)]"
                  : "border-[var(--border)] hover:border-[var(--pharma-blue)]"
              }`}
              style={
                isActive
                  ? { outline: "2px solid var(--pharma-blue)", outlineOffset: "2px" }
                  : undefined
              }
              data-placeholder-image="true"
              data-testid={`product-gallery-thumb-${s.key}`}
            >
              <ImagePlus className="w-6 h-6" strokeWidth={1.5} aria-hidden="true" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

const CharacteristicsTable = ({ product, compact = false }) => (
  <table
    className={`w-full border-collapse ${compact ? "text-[13px]" : "text-[14.5px]"}`}
    data-testid={compact ? "characteristics-mini" : "characteristics-table"}
  >
    <tbody>
      {CHARACTERISTIC_FIELDS.map(({ key, label }) => (
        <tr key={key} className="border-t border-[var(--border)]">
          <th
            scope="row"
            className={`text-left align-top font-medium text-[var(--charcoal)] ${
              compact ? "py-2 pr-3 w-[110px]" : "py-3 pr-6 w-[210px]"
            }`}
          >
            {label}
          </th>
          <td className={`align-top text-[var(--slate)] ${compact ? "py-2" : "py-3"}`}>
            {product.characteristics[key]}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = findProduct(slug);

  React.useEffect(() => {
    if (product) trackProductView(product.slug);
  }, [product]);

  if (!product) return <NotFound />;

  const path = `/products/${product.slug}`;
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.shortDescription,
    category: product.categoryLabel,
    url: canonical,
    brand: { "@type": "Brand", name: siteConfig.COMPANY_NAME },
    manufacturer: {
      "@type": "Organization",
      name: "Sourced through specialist manufacturing partners",
    },
  };

  const related = relatedProducts(product.slug);
  const isBlisterViolet = product.slug === "blister-pack-violet-ink";
  const displayH1 = product.pageH1 || product.name;
  const displayIntro = product.pageIntro || product.shortDescription;

  return (
    <>
      <SEO
        rawTitle={product.seoTitle}
        description={product.seoDescription}
        path={path}
        extraSchema={productSchema}
      />
      <Helmet />

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: product.categoryLabel, to: `/solutions/${product.solutionSlug}` },
            { label: product.name },
          ]}
        />
      </div>

      {/* Hero */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-12 lg:pb-16"
        data-testid={`product-hero-${product.slug}`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-10 lg:gap-14 items-start">
          <div>
            <p className="eyebrow" data-testid="product-category-eyebrow">
              {product.categoryLabel}
            </p>
            <h1
              className={`font-heading mt-4 ${isBlisterViolet ? "text-[36px] lg:text-[48px]" : "text-[32px] lg:text-[44px]"} leading-tight lg:leading-[1.05] font-semibold text-[var(--navy)]`}
              data-testid="product-h1"
            >
              {displayH1}
            </h1>
            <p className="mt-6 text-[16.5px] leading-relaxed text-[var(--charcoal)] max-w-2xl">
              {displayIntro}
            </p>
            <div className="mt-8">
              {isBlisterViolet ? (
                <ProductGallery product={product} />
              ) : (
                <div className="image-frame">
                  <ProductImage imageKey={product.heroImageKey} alt={product.heroAlt} productName={product.name} />
                </div>
              )}
            </div>
          </div>

          <aside className="lg:sticky lg:top-32 border border-[var(--border)] rounded-[4px] p-7 lg:p-8 bg-white">
            <p className="font-heading text-[13px] font-semibold uppercase text-[var(--slate)]" style={{ letterSpacing: "0.12em" }}>
              Enquire
            </p>
            <p className="font-heading mt-3 text-[21px] font-semibold text-[var(--navy)]">
              Request a sample or discuss application
            </p>
            <div className="mt-6 flex flex-col gap-3">
              <Link to={`/request-sample?product=${product.slug}`} className="btn-primary text-sm px-6 py-3 justify-center" data-testid="product-cta-sample">
                Request a Sample
              </Link>
              <Link to={`/contact?type=technical&product=${product.slug}`} className="btn-outline text-sm px-6 py-3 justify-center" data-testid="product-cta-technical">
                Discuss Application
              </Link>
            </div>
            <div className="mt-8">
              <p className="font-heading text-[12px] font-semibold uppercase text-[var(--slate)] mb-3" style={{ letterSpacing: "0.12em" }}>
                Key Characteristics
              </p>
              <CharacteristicsTable product={product} compact />
            </div>
          </aside>
        </div>
      </section>

      {/* Typical Applications */}
      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-12 lg:py-16 border-t border-[var(--border)]" data-testid="product-applications">
        <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
          Typical Applications
        </h2>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 max-w-4xl">
          {product.typicalApplications.map((a) => (
            <li key={a} className="flex items-start gap-2.5 text-[15px] text-[var(--charcoal)]">
              <Check strokeWidth={1.5} className="w-4 h-4 mt-1 shrink-0" style={{ color: "var(--pharma-blue)" }} aria-hidden="true" />
              <span>{a}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Suitable Substrates */}
      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-12 lg:py-16 border-t border-[var(--border)]" data-testid="product-substrates">
        <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
          Suitable Substrates
        </h2>
        <p className="mt-4 text-[15.5px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
          {product.substrates}
        </p>
      </section>

      {/* Key Product Characteristics — full */}
      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-12 lg:py-16 border-t border-[var(--border)]" data-testid="product-characteristics-full">
        <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
          Key Product Characteristics
        </h2>
        <p className="mt-3 text-[13px] text-[var(--slate)]">
          Technical values are confirmed on request against your specific application.
        </p>
        <div className="mt-6 max-w-3xl border border-[var(--border)] rounded-[4px] overflow-hidden">
          <CharacteristicsTable product={product} />
        </div>
      </section>

      {/* Documents (§11 + §7 — 3-card grid: TDS / SDS / Sample) */}
      <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-12 lg:py-16 border-t border-[var(--border)]" data-testid="product-documents">
        <h2 className="font-heading text-2xl lg:text-[32px] font-semibold text-[var(--navy)]">
          Documents
        </h2>
        <p className="mt-3 text-[14px] text-[var(--slate)] max-w-3xl">
          Technical and safety documentation is available on request. Submit the form and our team will send the relevant document for your application.
        </p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-[var(--border)] rounded-[4px] p-6 bg-white flex flex-col" data-testid="product-doc-tds">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-[var(--pharma-blue)]" strokeWidth={1.5} />
              <p className="font-heading text-[16px] font-semibold text-[var(--navy)]">Technical Data Sheet</p>
            </div>
            <p className="mt-2 text-[13.5px] text-[var(--slate)] flex-1">Application-specific technical information for this product.</p>
            <div className="mt-4">
              <Link
                to={`/contact?type=technical&resource=tds&product=${product.slug}`}
                className="btn-outline text-xs !py-2 !px-3 inline-flex"
                data-testid="product-doc-tds-cta"
              >
                Request TDS
              </Link>
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-[4px] p-6 bg-white flex flex-col" data-testid="product-doc-sds">
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-[var(--pharma-blue)]" strokeWidth={1.5} />
              <p className="font-heading text-[16px] font-semibold text-[var(--navy)]">Safety Data Sheet</p>
            </div>
            <p className="mt-2 text-[13.5px] text-[var(--slate)] flex-1">Handling, storage and safety information provided on request.</p>
            <div className="mt-4">
              <Link
                to={`/contact?type=technical&resource=sds&product=${product.slug}`}
                className="btn-outline text-xs !py-2 !px-3 inline-flex"
                data-testid="product-doc-sds-cta"
              >
                Request SDS
              </Link>
            </div>
          </div>
          <div className="border border-[var(--border)] rounded-[4px] p-6 bg-white flex flex-col" data-testid="product-doc-sample">
            <div className="flex items-center gap-3">
              <FlaskConical className="w-6 h-6 text-[var(--pharma-blue)]" strokeWidth={1.5} />
              <p className="font-heading text-[16px] font-semibold text-[var(--navy)]">Product Sample</p>
            </div>
            <p className="mt-2 text-[13.5px] text-[var(--slate)] flex-1">Evaluate the product for your application.</p>
            <div className="mt-4">
              <Link
                to={`/request-sample?product=${product.slug}`}
                className="btn-outline text-xs !py-2 !px-3 inline-flex"
                data-testid="product-doc-sample-cta"
              >
                Request Sample
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Application Review */}
      <section className="bg-[var(--bg-alt)]" data-testid="product-application-review">
        <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20">
          <div className="max-w-3xl">
            <p className="eyebrow">Application Review</p>
            <h2 className="font-heading mt-4 text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
              Share the details of your application
            </h2>
            <p className="mt-4 text-[15.5px] leading-relaxed text-[var(--charcoal)]">
              Provide the following details so our technical team can evaluate whether this product is suitable for your production application.
            </p>
          </div>
          <ol className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 max-w-4xl">
            {product.applicationReviewPrompts.map((p, idx) => (
              <li key={p} className="flex items-start gap-3 text-[15px] text-[var(--charcoal)]">
                <span
                  className="font-heading font-semibold text-[13px] w-6 h-6 rounded-full border flex items-center justify-center shrink-0"
                  style={{ borderColor: "var(--pharma-blue)", color: "var(--navy)" }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span>{p}</span>
              </li>
            ))}
          </ol>
          <div className="mt-10">
            <Link
              to={`/request-sample?product=${product.slug}`}
              className="btn-primary text-sm"
              data-testid="product-cta-request-review"
            >
              Request Product Sample
            </Link>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-20" data-testid="product-related">
          <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-[var(--navy)]">
            Related products
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/products/${r.slug}`}
                className="border border-[var(--border)] rounded-[4px] p-6 flex flex-col hover:bg-[var(--bg-alt)] transition-colors"
                data-testid={`related-product-${r.slug}`}
              >
                <p className="text-[12px] uppercase tracking-[0.12em] font-semibold text-[var(--pharma-blue)]">
                  {r.categoryLabel}
                </p>
                <p className="font-heading mt-2 text-[17px] font-semibold text-[var(--navy)]">
                  {r.name}
                </p>
                <p className="mt-3 text-[14px] leading-relaxed text-[var(--slate)] flex-1">
                  {r.shortDescription.split(". ")[0]}.
                </p>
                <span className="arrow-link mt-4">
                  View Product <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Large closer CTA — Blister Pack Violet Ink only (§10) */}
      {isBlisterViolet && (
        <section className="bg-[var(--navy)] text-white" data-testid="product-large-closer">
          <div className="mx-auto max-w-[1240px] px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-8 items-center">
            <div>
              <p className="text-[12px] uppercase tracking-[0.14em] text-white/70 font-semibold">Alternative Supply</p>
              <h2 className="font-heading mt-4 text-[28px] lg:text-[38px] leading-tight font-semibold">
                Evaluating an Alternative Blister Printing Ink?
              </h2>
              <p className="mt-4 text-[16px] lg:text-[17px] leading-relaxed text-white/85 max-w-2xl">
                Send us details of your current ink, printing process and substrate. Our team will review the application.
              </p>
            </div>
            <div className="flex lg:justify-end">
              <Link
                to={`/request-sample?product=${product.slug}`}
                className="inline-flex items-center gap-2 bg-white text-[var(--navy)] px-6 py-3 rounded-[4px] text-sm font-semibold hover:bg-white/90 transition-colors"
                data-testid="product-large-closer-cta"
              >
                Request a Sample <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ProductDetailPage;
